import {Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormControl, FormGroupDirective, NgForm, Validators} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {ErrorStateMatcher, MatOption} from '@angular/material/core';
import {ReferenceBookResponse, ReferenceBookType} from '@npck/af-frontend-api';
import {LocalizationService, Spinner} from '@npck/libs/commons';
import {isArray} from 'lodash-es';
import {BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, startWith, Subscription} from 'rxjs';
import {DictionaryHolderService} from '../../domain/dictionary-holder.service';

const MIN_ITEMS_COUNT_FOR_SEARCH = 10;
export const SELECT_ALL_VALUE = 'selectAll';
export const CLEAR_VALUE = 'clearValue';

class ParentControlErrorStateMatcher implements ErrorStateMatcher {
    constructor(private parentControl: FormControl) {
    }

    isErrorState(control: FormControl | null, _form: FormGroupDirective | NgForm | null): boolean {
        return this.parentControl?.invalid && (control?.touched || this.parentControl?.touched);
    }
}

@Component({
    selector: 'af-dictionary-selector',
    templateUrl: './dictionary-selector.component.html',
    styleUrls: ['dictionary-selector.component.scss'],
    standalone: false,
})
export class DictionarySelectorComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
    @ViewChild('selectAll') selectAll: MatOption;
    @Input() control: FormControl<string> | FormControl<string[]>;
    @Input() translationPrefix: string = '';
    @Input() label: string;
    @Input() hintTranslationKey: string;
    @Input() tooltip: string;
    @Input() showCode = false;
    @Input() showCodeWithName = false;
    @Input() placeholder: string;
    @Input() selectedCountPlaceholder: string;
    @Input() dictionaryName: string;
    @Input() filterDictionaryBy: string[];
    @Input() excludeFromDictionary: string[];
    @Input() excludeItemsWithCode1 = false;
    @Input() dictionary: ReferenceBookResponse[] = [];
    @Input() predefinedValue: string | string[];
    @Input() isDependentDictionary = false;
    @Input() shouldSortDictionary = false;
    @Input() isMultiple = false;
    @Input() isParentDictionaryMultiple = false;
    @Input() shouldAddSelectAll = false;
    @Input() shouldAddClearOption = false;
    @Input() clearOptionLabel = 'i18n_af_notSelected';
    @Input() hasSearch = false;
    @Input() shouldAlwaysShowSelectedCount = false;
    @Input() selectAllLabel = 'i18n_af_selectAll';
    @Input() parentDictionaryFormControl: FormControl<string> | FormControl<string[]>;
    @Input() disabled: boolean = false;
    @Input() resyncOnControlChange = false;
    @Input() parentDictionaryField: keyof ReferenceBookResponse = 'code';
    @Input() clearUnlistedValueOnOpen = false;
    @Input() unlistedValueLabel = '';
    @Output() dictionaryLoaded = new EventEmitter<ReferenceBookResponse[]>();
    SELECT_ALL_VALUE = SELECT_ALL_VALUE;
    CLEAR_VALUE = CLEAR_VALUE;
    searchControl = new FormControl<string>(null);
    selectedItems: ReferenceBookResponse[] = [];
    controlErrorStateMatcher: ErrorStateMatcher;
    isSelectPanelOpen = false;

    Validators = Validators;
    Spinner = Spinner;
    filteredDictionary: ReferenceBookResponse[] = [];
    private searchQuery$ = new BehaviorSubject<string>('');
    private parentValue$ = new BehaviorSubject<string | string[]>('');

    private dictionaryLoaded$ = new BehaviorSubject<boolean>(false);
    private parentAndSearchSubscription: Subscription;
    private controlStatusSubscription: Subscription;

    private lastParentValue: string | string[] | null = null;

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private localizationService: LocalizationService,
    ) {
    }

    get isSelectAllSelected(): boolean {
        return (isArray(this.control.value) && this.control.value.length === 1 && this.control.value.includes(SELECT_ALL_VALUE))
            || this.control.value === SELECT_ALL_VALUE;
    }

    get shouldHandleUnlistedValue(): boolean {
        const value = this.control.value as string;
        return this.clearUnlistedValueOnOpen
            && value !== null
            && !this.dictionary.some(item => item.code === value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.filterDictionaryBy
            && !changes.filterDictionaryBy.isFirstChange()
            && this.isMultiple
            && !(changes.filterDictionaryBy?.previousValue as string[])?.every(p => changes.filterDictionaryBy.currentValue.includes(p))) {
            this.reloadDictionary();
        } else if (changes.predefinedValue?.isFirstChange()) {
            this.reloadDictionary();
        }
        if (changes.excludeFromDictionary && !changes.excludeFromDictionary.isFirstChange()) {
            this.reloadDictionary();
        }
        if (this.resyncOnControlChange && changes.control && !changes.control.isFirstChange()) {
            this.controlErrorStateMatcher = new ParentControlErrorStateMatcher(this.control as FormControl);
            this.controlStatusSubscription?.unsubscribe();
            this.subscribeToControlStatusChanges();
            this.syncSelectedItemsFromControl();
            if (this.control.disabled) {
                this.searchControl.disable({emitEvent: false});
            } else if (!this.disabled && !this.isDependentDictionary) {
                this.searchControl.enable({emitEvent: false});
            }
        }
    }

    ngOnInit(): void {
        this.controlErrorStateMatcher = new ParentControlErrorStateMatcher(this.control as FormControl);
        this.subscribeToControlStatusChanges();
        this.syncSearchControlDisabledState();
        this.subscribeToCombinedParentAndSearchFilter();
        this.subscribeToParentDictionary();
        if ((!this.dictionaryName && !this.dictionary?.length) || (this.dictionaryName && this.dictionary?.length)) {
            this.filteredDictionary = this.dictionary;
            if (!this.disabled) {
                this.control.enable({emitEvent: false});
            }
            this.dictionaryLoaded$.next(true);
            return;
        }
        this.reloadDictionary();
    }

    ngDoCheck(): void {
        this.syncSearchControlDisabledState();
    }

    ngOnDestroy(): void {
        this.controlStatusSubscription?.unsubscribe();
        this.parentAndSearchSubscription?.unsubscribe();
    }

    handleSelection(event: MatAutocompleteSelectedEvent): void {
        if (event.option.value === 'selectAll') {
            this.toggleAll();
            return;
        }
        if (event.option.value === CLEAR_VALUE) {
            (this.control as FormControl<string>).setValue(null);
            this.selectedItems = [];
            this.updateDisplayedValue();
            return;
        }
        const selectedItem = this.filteredDictionary.find(item => item.code === event.option.value);
        if (this.isMultiple) {
            const index = this.selectedItems.findIndex(item => item.code === selectedItem.code);
            if (index > -1) {
                this.selectedItems.splice(index, 1);
            } else {
                this.selectedItems.push(selectedItem);
            }
            const value = this.selectedItems.length > 0 ? this.selectedItems.map(item => item.code) : null;
            (this.control as FormControl<string[]>).setValue(value);
        } else {
            this.selectedItems = [selectedItem];
            (this.control as FormControl<string>).setValue(selectedItem.code);
        }
        this.updateDisplayedValue();
    }

    onSelectOpened(opened: boolean): void {
        this.isSelectPanelOpen = opened;
        if (opened && this.shouldHandleUnlistedValue) {
            (this.control as FormControl<string>).setValue(null);
            this.control.markAsTouched();
        }
    }

    isChecked(item: ReferenceBookResponse): boolean {
        return this.selectedItems.findIndex(selectedItem => selectedItem.code === item.code) > -1;
    }

    displayName(item: ReferenceBookResponse): string {
        if (this.showCodeWithName) {
            return `${item.code} - ${item.name_ru}`;
        }
        return this.showCode ? item.code1 : item.name_ru;
    }

    updateDisplayedValue(): void {
        let value;
        if (this.selectedItems.length === 0 && this.isSelectAllSelected) {
            value = this.localizationService.getLocalizedKey(this.selectAllLabel);
        } else {
            const shouldShowCount = this.shouldAlwaysShowSelectedCount || this.selectedItems.length > 3;
            value = shouldShowCount && this.selectedItems.length > 0
                ? this.localizationService
                    .getLocalizedKey(this.selectedCountPlaceholder, {count: this.selectedItems.length.toString()})
                : this.selectedItems.map(item => this.displayName(item)).join(', ');
            this.searchControl.reset();
        }
        this.searchControl.setValue(value, {emitEvent: false});
    }

    clearSearchField(): void {
        this.searchControl.setValue(null, {emitEvent: false});
    }

    toggleAll(): void {
        if (this.isMultiple) {
            let filteredDictionary = this.dictionary;
            if (this.isDependentDictionary) {
                filteredDictionary = this.dictionary.filter(item => item.code1 === this.parentDictionaryFormControl.value);
            }
            if (this.selectedItems.length !== filteredDictionary.length) {
                this.selectedItems = [...filteredDictionary];
                (this.control as FormControl<string[]>).setValue(this.selectedItems.map(item => item.code));
                this.selectAll.select();
            } else {
                this.selectedItems = [];
                (this.control as FormControl<string[]>).setValue(null);
            }
        } else {
            this.selectedItems = [];
        }
        this.updateDisplayedValue();
    }

    private reloadDictionary(): void {
        if (this.dictionaryName) {
            Spinner.show(this.dictionaryName);
            this.dictionaryHolderService.getReference(this.dictionaryName)
                .subscribe(response => {
                    if (this.isDependentDictionary || this.disabled) {
                        this.disableControl();
                    }
                    let dictionary = response;
                    if (this.shouldSortDictionary) {
                        dictionary = response.sort((a, b) => a.name_ru.localeCompare(b.name_ru));
                    }
                    this.dictionary = dictionary;
                    if (this.filterDictionaryBy) {
                        dictionary = dictionary.filter(item => [item.code, item.code1, item.code2, item.name_ru]
                            .some(value => this.filterDictionaryBy.includes(value)));
                        this.dictionary = dictionary;
                    }

                    if (this.excludeFromDictionary) {
                        dictionary = dictionary.filter(item => ![item.code, item.code1, item.code2, item.name_ru]
                            .some(value => this.excludeFromDictionary.includes(value)));
                        this.dictionary = dictionary;
                    }
                    if (this.excludeItemsWithCode1) {
                        dictionary = dictionary.filter(item => !item.code1);
                        this.dictionary = dictionary;
                    }
                    this.filteredDictionary = dictionary;
                    this.dictionaryLoaded.emit(this.dictionary);
                    if (this.predefinedValue && dictionary.length) {
                        const dictionaryValue = dictionary.filter(item => [item.code, item.code1, item.code2, item.name_ru]
                            .some(value => {
                                if (isArray(this.predefinedValue)) {
                                    return this.predefinedValue.includes(value);
                                }
                                return this.predefinedValue === value;
                            }));
                        if (this.isMultiple) {
                            const value = dictionaryValue.length ? dictionaryValue.map(item => item.code) : null;
                            (this.control as FormControl<string[]>).setValue(value);
                        } else {
                            (this.control as FormControl<string>).setValue(dictionaryValue.length ? dictionaryValue[0].code : null);
                        }
                        this.selectedItems = dictionaryValue;
                        this.updateDisplayedValue();
                    }
                    this.syncSelectedItemsFromControl();
                    if (this.isSelectAllSelected) {
                        this.updateDisplayedValue();
                    }
                    Spinner.hide(this.dictionaryName);
                    this.subscribeToSearchControlChanges();
                    this.parentDictionaryFormControl && this.parentValue$.next(this.parentDictionaryFormControl.value);
                    this.dictionaryLoaded$.next(true);
                });
        } else {
            this.updateDisplayedValue();
        }
    }

    private syncSelectedItemsFromControl(): void {
        if (!this.dictionary?.length) {
            return;
        }

        if (this.isMultiple) {
            const controlValue = this.control.value as string[];
            if (controlValue?.length) {
                this.selectedItems = this.dictionary.filter(item => controlValue.includes(item.code));
            } else {
                this.selectedItems = [];
            }
        } else {
            const controlValue = this.control.value as string;
            if (controlValue && controlValue !== SELECT_ALL_VALUE) {
                const selectedItem = this.dictionary.find(item => item.code === controlValue);
                this.selectedItems = selectedItem ? [selectedItem] : [];
            } else {
                this.selectedItems = [];
            }
        }
        this.updateDisplayedValue();
    }

    private disableControl(): void {
        if (this.isMultiple) {
            this.control.disable({emitEvent: false});
        } else {
            this.control.disable({emitEvent: false});
            this.searchControl.disable({emitEvent: false});
        }
    }

    private syncSearchControlDisabledState(): void {
        if (this.isMultiple) {
            return;
        }
        if (this.control.disabled && this.searchControl.enabled) {
            this.searchControl.disable({emitEvent: false});
            return;
        }
        if (this.control.enabled && this.searchControl.disabled && !this.disabled && !this.isDependentDictionary) {
            this.searchControl.enable({emitEvent: false});
        }
    }

    private enableControl(): void {
        if (this.disabled) {
            return;
        }
        if (this.isMultiple) {
            this.control.enable({emitEvent: false});
        } else {
            this.control.enable({emitEvent: false});
            this.searchControl.enable({emitEvent: false});
        }
    }

    private resetControl(): void {
        if (this.isMultiple) {
            this.control.reset();
        } else {
            this.control.reset();
            this.searchControl.reset();
        }
        this.selectedItems = [];
        this.updateDisplayedValue();
        this.searchQuery$.next('');
    }

    private subscribeToControlStatusChanges(): void {
        this.controlStatusSubscription = this.control.statusChanges.subscribe(status => {
            if (status === 'DISABLED') {
                this.searchControl.disable({emitEvent: false});
            } else if (!this.disabled && !this.isDependentDictionary) {
                this.searchControl.enable({emitEvent: false});
            }
        });
    }

    private subscribeToParentDictionary(): void {
        if (this.isParentDictionaryMultiple) {
            (this.parentDictionaryFormControl as FormControl<string[]>)?.valueChanges
                ?.pipe(
                    startWith(this.parentDictionaryFormControl?.value),
                    distinctUntilChanged(),
                    filter(value => !!value),
                )
                .subscribe(value => this.parentValue$.next(value));
        } else {
            (this.parentDictionaryFormControl as FormControl<string>)?.valueChanges
                ?.pipe(
                    startWith(this.parentDictionaryFormControl?.value),
                    distinctUntilChanged(),
                    filter(value => !!value),
                )
                .subscribe(value => this.parentValue$.next(value));
        }
    }

    private subscribeToSearchControlChanges(): void {
        if (this.dictionary.length >= MIN_ITEMS_COUNT_FOR_SEARCH || this.hasSearch) {
            this.searchControl.valueChanges
                .pipe(
                    debounceTime(500),
                    distinctUntilChanged(),
                )
                .subscribe(value => this.searchQuery$.next(value || ''));
        }
    }

    private subscribeToCombinedParentAndSearchFilter(): void {
        this.parentAndSearchSubscription?.unsubscribe();
        this.parentAndSearchSubscription = combineLatest([
            this.searchQuery$.pipe(startWith('')),
            this.parentValue$.pipe(startWith('')),
        ]).subscribe(([searchValue, parentValue]) => {
            let filtered = this.dictionary;
            if (this.isDependentDictionary && parentValue) {
                this.enableControl();
                filtered = this.filterByParentValue(filtered, parentValue);
            }
            if (searchValue && this.dictionary.length >= MIN_ITEMS_COUNT_FOR_SEARCH || this.hasSearch) {
                filtered = this.filteredDictionaryBy(filtered, searchValue);
            }
            this.filteredDictionary = filtered;
            const parentChanged = parentValue !== this.lastParentValue;
            this.lastParentValue = parentValue;

            if (this.isDependentDictionary && this.isMultiple && parentChanged) {
                this.selectedItems = [];
            }

            if (this.isDependentDictionary && !this.isMultiple && parentChanged) {
                const currentValue = this.control?.value as string;
                if (currentValue && !this.filteredDictionary.some(item => item.code === currentValue)) {
                    this.resetControl();
                }
            }
            if (this.isDependentDictionary && !this.isMultiple && parentValue && filtered.length === 1) {
                let value;
                switch (this.parentDictionaryField) {
                    case 'code1':
                        value = this.filteredDictionary[0].code1;
                        break;
                    case 'code2':
                        value = this.filteredDictionary[0].code2;
                        break;
                    case 'name_ru':
                        value = this.filteredDictionary[0].name_ru;
                        break;
                    default:
                        value = this.filteredDictionary[0].code;
                        break;
                }
                (this.control as FormControl<string>).setValue(value);
                (this.control as FormControl<string>).disable();
                (this.searchControl as FormControl<string>).disable();
                this.selectedItems = this.filteredDictionary;
                this.updateDisplayedValue();
            }
            if (this.isDependentDictionary && this.predefinedValue) {
                const dictionaryValue = filtered.filter(item => [item.code, item.code1, item.code2, item.name_ru]
                    .some(value => {
                        if (isArray(this.predefinedValue)) {
                            return this.predefinedValue.includes(value);
                        }
                        return this.predefinedValue === value;
                    }));
                const hasPredefinedValue = !!dictionaryValue.length;
                if (parentValue && !hasPredefinedValue) {
                    this.predefinedValue = null;
                    this.resetControl();
                }
            }
        });
    }

    private filterByParentValue(
        dictionary: ReferenceBookResponse[],
        parentValue: string | string[],
    ): ReferenceBookResponse[] {
        if (Array.isArray(parentValue)) {
            return dictionary.filter(item => [item.code, item.code1, item.code2, item.name_ru]
                .some(value => parentValue.includes(value)));
        }
        return dictionary.filter(item => [item.code, item.code1, item.code2, item.name_ru]
            .some(value => value === parentValue));
    }

    private filteredDictionaryBy(dictionary: ReferenceBookResponse[], filterValue: string | string[]): ReferenceBookResponse[] {
        if ('string' === typeof filterValue) {
            const lowerFilter = filterValue.toLowerCase();
            const dictionaryType = this.dictionaryName?.toUpperCase();
            const isExactMatch = dictionaryType === ReferenceBookType.TRANSACTION_TYPE
                || dictionaryType === ReferenceBookType.FRAUD_TYPE;
            return dictionary.filter(item => {
                const valuesToCheck = isExactMatch
                    ? [item.code1]
                    : [item.code, item.code1, item.code2, item.name_ru];
                return valuesToCheck
                    .filter(value => value?.trim().length > 0)
                    .some(value => {
                        const lowerValue = value.toLowerCase();
                        return isExactMatch
                            ? lowerValue === lowerFilter
                            : lowerValue.includes(lowerFilter);
                    });
            });
        } else {
            return dictionary.filter(item => [item.code, item.code1, item.code2, item.name_ru].some(value => filterValue.includes(value)));
        }
    }
}
